import { HttpService } from './http';
import { contractTemplateCodes, parseInvoice, parsePartnerContract } from './partner';
export class StripeService {
  static async createCheckoutSession({
    partnerCode,
    priceId,
    productId,
    customPrice,
    customPriceRecurrence,
    oneShotPayment,
    successUrl,
    cancelUrl,
    currency
  }) {
    try {
      const params = {};
      if (priceId) params.price_id = priceId;
      if (productId) params.product_id = productId;
      if (oneShotPayment !== undefined) {
        params.subscription = oneShotPayment ? 'false' : 'true';
      }
      if (successUrl) params.success_url = successUrl;
      if (cancelUrl) params.cancel_url = cancelUrl;
      if (customPrice !== undefined) {
        params.price_value_in_cents = customPrice;
        if (customPriceRecurrence !== undefined) params.recurring_interval = customPriceRecurrence === 'annual' ? 'year' : 'month';
      }
      if (currency) params.currency = currency;
      const {
        stripe_url
      } = await HttpService.post('v2', `/billing/stripe/checkout_session`, [], [], JSON.stringify(params), {
        partner: partnerCode
      });
      return stripe_url;
    } catch (err) {
      console.error('[StripeService][createCheckoutSession]', err);
      throw new Error(err);
    }
  }
  static async createPortalSession({
    code: partner
  }) {
    try {
      const {
        stripe_url
      } = await HttpService.post('v1', `/billing/stripe/portal_session`, [], [], null, {
        partner
      });
      return stripe_url;
    } catch (err) {
      console.error('[StripeService][createPortalSession]', err);
      throw new Error(err);
    }
  }
  static async getOffers({
    partnerCode,
    contractCodes,
    eventCode
  }) {
    try {
      const queryParams = [];
      if (contractCodes) {
        const templateCodes = Object.entries(contractTemplateCodes).filter(([, code]) => contractCodes.includes(code)).map(([templateCode]) => templateCode);
        queryParams.push({
          key: 'contract_codes',
          value: templateCodes.join(',')
        });
      }
      if (eventCode) queryParams.push({
        key: 'event',
        value: eventCode
      });
      const offers = await HttpService.get('v1', '/billing/stripe/offers', queryParams, [], null, {
        partner: partnerCode
      });
      return offers.map(({
        id,
        contract_code,
        description,
        description_long,
        features,
        event,
        prices
      }) => ({
        id,
        contractCode: contract_code && contractTemplateCodes[contract_code] || '',
        description: description || '',
        descriptionLong: description_long || '',
        features,
        event: event || undefined,
        prices: prices.map(({
          id,
          company_workforce_count_limit,
          interval,
          unit_amount_in_cents
        }) => ({
          id,
          companyMaxSize: company_workforce_count_limit,
          periodType: interval,
          price: unit_amount_in_cents / 100
        }))
      }));
    } catch (err) {
      console.error('[StripeService][getOffers]', err);
      throw err;
    }
  }
  static async getInvoices({
    partner
  }) {
    try {
      const {
        results
      } = await HttpService.get('v1', `/partners/${partner.id}/stripe_invoices`, [], [], null, {
        partner: partner.code
      });
      return results.map(parseInvoice);
    } catch (err) {
      console.error('[StripeService][getInvoices]', err);
      throw err;
    }
  }
  static async createInvoice({
    partner,
    contactEmail,
    ...props
  }) {
    try {
      let params = {
        partner: partner.id,
        customer_email: contactEmail
      };
      if ('customPrice' in props) {
        params = {
          ...params,
          stripe_product_id: props.stripeProductId,
          custom_price_value_in_cents: props.customPrice,
          custom_recurring_interval: props.customPricePeriodType
        };
      } else {
        params['stripe_product_price_id'] = props.stripePriceId;
      }
      const res = await HttpService.post('v1', `/billing/stripe/subscription_intents`, [], [], JSON.stringify(params), {
        partner: partner.code
      });
      return parseInvoice(res);
    } catch (err) {
      console.error('[StripeService][createInvoice]', err);
      throw err;
    }
  }
  static async createPartnerContractWithInvoice({
    partner,
    stripeCustomerId,
    automaticallyRenew,
    invoiceAdditionalValue,
    daysUntilDue,
    ...props
  }) {
    try {
      let params = {
        partner_id: partner.id,
        automatically_renew: automaticallyRenew,
        stripe_customer_id: stripeCustomerId
      };
      if (invoiceAdditionalValue?.length) {
        params = {
          ...params,
          additional_label: 'Mention spéciale:',
          additional_value: invoiceAdditionalValue
        };
      }
      if (daysUntilDue != null && daysUntilDue >= 0) {
        params = {
          ...params,
          days_until_due: daysUntilDue
        };
      }
      const hasCustomPrice = 'customPrice' in props;
      if (hasCustomPrice) {
        params = {
          ...params,
          stripe_product_id: props.stripeProductId,
          custom_price_value_in_cents: props.customPrice
        };
      } else {
        params['stripe_product_price_id'] = props.stripePriceId;
      }
      if (props.startDate) {
        params = {
          ...params,
          start_date: props.startDate.format('YYYY-MM-DD')
        };
      }
      if (hasCustomPrice && !automaticallyRenew && props.startDate && props.endDate) {
        params = {
          ...params,
          end_date: props.endDate.format('YYYY-MM-DD')
        };
      }
      const res = await HttpService.post('v1', `/billing/create_partner_with_stripe_invoice`, [], [], JSON.stringify(params), {
        partner: partner.code
      });
      return parsePartnerContract(res);
    } catch (err) {
      console.error('[StripeService][createPartnerContractWithInvoice]', err);
      throw err;
    }
  }
}